import {
  Button,
  Flex,
  Modal,
  ModalContent,
  ModalOverlay,
  VStack,
  FormControl,
  Box,
  useToast,
  Text,
  Divider,
  ModalCloseButton,
} from '@chakra-ui/react';
import { AddressElement, useElements } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';
import CustomToast from '../../../common/CustomToast';
import sentry from '../../../sentry';

const UpdateBillingContactModal = ({
  isOpen,
  onClose,
  currentBillingContact,
  onSubmit,
}) => {
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (currentBillingContact) {
      setName(currentBillingContact.name || '');
    }
  }, [currentBillingContact]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (!elements) {
        throw new Error('Stripe has not been initialized.');
      }
      const addressElement = elements.getElement(AddressElement);
      const {
        complete,
        value: addressDetails,
        error: addressError,
      } = await addressElement.getValue();
      if (!complete) {
        throw new Error('Please fill in all required fields.');
      }
      if (addressError) {
        throw new Error(addressError.message);
      }
      if (
        !addressDetails.address.postal_code ||
        !addressDetails.address.country
      ) {
        throw new Error('Please provide a complete address.');
      }
      if (!addressDetails.phone) {
        throw new Error('Please provide a phone number.');
      }
      const billingDetails = {
        name: name || addressDetails.name,
        email: addressDetails.email,
        phone: addressDetails.phone,
        address: addressDetails.address,
      };

      if (onSubmit) {
        await onSubmit(billingDetails);
      }

      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Billing details updated"
            description="You have successfully updated your billing details."
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });

      onClose();
    } catch (error) {
      sentry.sendError(error);
      console.error('Error updating billing details:', error);

      const errorMessage =
        error.response?.data?.error || error.message || 'Please try again.';

      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Failed to update billing details"
            description={errorMessage}
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      data-testid="update-payment-details-modal"
    >
      <ModalOverlay />
      <ModalContent pt={8} pb={6} px={6}>
        <ModalCloseButton />
        <Text textStyle="heading4" textAlign="center">
          Billing details
        </Text>
        <Divider my="10px" />
        <form
          onSubmit={handleSubmit}
          style={{ width: '100%', fontFamily: 'Inter, sans-serif' }}
        >
          <VStack spacing={6} my={2}>
            <FormControl>
              <Box p={3} bg="white" mt={2}>
                <AddressElement
                  options={{
                    mode: 'billing',

                    fields: {
                      name: 'always',
                      email: 'always',
                      phone: 'always',
                    },
                    validation: {
                      name: { required: 'always' },
                      email: { required: 'always' },
                      phone: { required: 'always' },
                    },
                    display: { name: 'organization' },
                    defaultValues: {
                      name: currentBillingContact?.name || '',
                      email: currentBillingContact?.email || '',
                      phone: currentBillingContact?.phone || '',
                      address: {
                        line1: currentBillingContact?.address?.line1 || '',
                        line2: currentBillingContact?.address?.line2 || '',
                        city: currentBillingContact?.address?.city || '',
                        state: currentBillingContact?.address?.state || '',
                        postal_code:
                          currentBillingContact?.address?.postal_code || '',
                        country: currentBillingContact?.address?.country || '',
                      },
                    },
                  }}
                />
              </Box>
            </FormControl>
          </VStack>
          <Flex direction="column" gap={3} mt={8} width="100%">
            <Button type="submit" isLoading={isLoading} w="100%">
              Save
            </Button>

            <Button
              variant="link"
              onClick={onClose}
              isDisabled={isLoading}
              w="100%"
            >
              <Text textStyle="bodySmall">Cancel</Text>
            </Button>
          </Flex>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default UpdateBillingContactModal;
